import Phaser from 'phaser';

import { pieceMovesets, moveSpriteGrid, SMALL_PIECES } from 'helpers/constants';
import { tileInRect } from 'helpers/mapHelpers';
import { getPieceTypeByIndex } from 'helpers/pieceHelpers';

export default class PieceInfoScene extends Phaser.Scene {
  constructor() {
    super('PieceInfoScene');
  }
  init(data) {
    const scale = this.game.defaultZoom;
    this.levelScene = data.scene;
    this.rect = new Phaser.Geom.Rectangle(0, 0, 91 * scale, 90 * scale);
    this.currentPiece = null;

    let overlayRect = new Phaser.Geom.Rectangle(
      0,
      0,
      this.game.canvas.width,
      this.game.canvas.height
    );
    this.backdrop = this.drawBackdrop(overlayRect, 0, false);
    this.backdrop
      .setInteractive(overlayRect, () => {
        return true;
      })
      .setAlpha(0);
    this.backdrop.on('pointerdown', this.handleClick);

    this.bg = this.add
      .sprite(this.rect.x, this.rect.y, 'ui_piece_detail')
      .setOrigin(0)
      .setScale(scale)
      .setPosition(this.rect.x, this.rect.y)
      .setDepth(10)
      .setAlpha(0);

    this.typeLabel = this.add
      .text(0, 0, 'Pawn', {
        fontSize: `${scale * 8}px`,
        fontFamily: 'pixelMix',
        color: '#5b87a1',
        align: 'center'
      })
      .setDepth(10)
      .setAlpha(0)
      .setOrigin(0.5)
      .setResolution(8);

    this.hpLabel = this.add
      .text(0, 0, 'HP:', {
        fontSize: `${scale * 8}px`,
        fontFamily: 'pixelMix',
        color: '#5b87a1',
        align: 'center'
      })
      .setDepth(10)
      .setAlpha(0)
      .setOrigin(0.5)
      .setResolution(8);

    this.movesetGroup = this.add.group();
    this.hpPips = this.add.group();
    let pip1 = this.add
      .sprite(0, 0, 'ui_hp_pip', 1)
      .setScale(scale)
      .setDepth(11)
      .setAlpha(0);
    let pip2 = this.add
      .sprite(0, 0, 'ui_hp_pip', 1)
      .setScale(scale)
      .setDepth(11)
      .setAlpha(0);
    this.hpPips.add(pip1);
    this.hpPips.add(pip2);

    this.nameLabel = this.add
      .text(0, 0, 'Name', {
        fontSize: `${scale * 16}px`,
        fontFamily: 'Astalemtim',
        color: '#f5f2d0',
        align: 'center'
      })
      .setDepth(12)
      .setAlpha(0)
      .setOrigin(0.5)
      .setResolution(8);

    this.pointy = this.add
      .sprite(0, 0, 'ui_tail')
      .setScale(scale)
      .setDepth(11)
      .setAlpha(0);

    this.renderModal(0);
    this.showing = false;
  }
  drawBackdrop(rect) {
    let gfx = this.add.graphics();
    gfx.lineStyle(this.game.defaultZoom, 0x5b87a1, 1);
    gfx.fillStyle(0x1f272c, 0);
    gfx.fillRectShape(rect);
    return gfx;
  }
  renderModal(alpha) {
    const scale = this.game.defaultZoom;
    this.bg.setPosition(this.rect.x, this.rect.y).setAlpha(alpha);
    let pointyY = this.rect.y + this.rect.height - 24 * scale;
    if (this.tooTall) {
      pointyY = this.rect.y + 40 * scale;
    }
    this.pointy.setPosition(
      this.rect.x + 11 * scale - (scale > 2 ? 2 : 1),
      pointyY
    );
    this.pointy.setAlpha(alpha);

    if (this.currentPiece) {
      let pieceType = getPieceTypeByIndex(this.currentPiece.tile.index);
      this.typeLabel.text = pieceType;
      this.typeLabel
        .setPosition(
          this.rect.x + this.rect.width / 2 - 0 * scale,
          this.rect.y + 36 * scale
        )
        .setAlpha(1)
        .setDepth(11);
      this.nameLabel.text =
        (this.levelScene.pieceMap[this.currentPiece.id] &&
          this.levelScene.pieceMap[this.currentPiece.id].name) ||
        'Name';
      this.nameLabel
        .setPosition(
          this.rect.x + this.rect.width / 2 - 0 * scale,
          this.rect.y + 17 * scale
        )
        .setAlpha(1)
        .setDepth(11);
      this.hpLabel.text = 'HP:';
      this.hpLabel
        .setPosition(
          this.rect.x + this.rect.width / 2 - 9 * scale,
          this.rect.y + 72 * scale
        )
        .setAlpha(1)
        .setDepth(11);
      this.hpPips.getChildren().forEach((pip, index) => {
        const hp = this.currentPiece.hp || 1;
        const total = this.currentPiece.hp ? 2 : 1;
        const frame = hp >= index + 1 ? 1 : 0;
        pip
          .setPosition(
            this.rect.x + this.rect.width / 2 + 3 * scale + index * (9 * scale),
            this.rect.y + 72 * scale - (scale === 1 ? 1 : 0)
          )
          .setAlpha(index + 1 <= total ? 1 : 0)
          .setDepth(11)
          .setFrame(frame);
      });
      this.renderMovementGrid(pieceType);
    }
  }
  renderMovementGrid(pieceType) {
    const scale = this.game.defaultZoom;
    let origin = {
      x: this.rect.x + this.rect.width / 2 - 9 * scale,
      y: this.rect.y + 45 * scale
    };
    let pieceMoveset = pieceMovesets[pieceType];
    if (this.movesetGroup && this.movesetGroup.scene) {
      this.movesetGroup.clear(true, true);
    } else {
      this.movesetGroup = this.add.group();
    }
    if (!pieceMoveset) {
      return;
    }
    pieceMoveset.forEach((row, rowIndex) => {
      row.forEach((moveType, colIndex) => {
        let tile = this.add
          .sprite(
            origin.x + colIndex * (4 * scale),
            origin.y + rowIndex * (4 * scale),
            'ui_grid_pieces',
            moveSpriteGrid[moveType]
          )
          .setScale(scale)
          .setDepth(11);

        this.movesetGroup.add(tile);
      });
    });
  }
  showPiece = (pointer, piece) => {
    if (!this.showing && !this.currentPiece) {
      this.showing = true;
      this.currentPiece = piece;
      this.pieceOriginalY = piece.parentContainer.y;
      piece.setDepth(15);
      this.add.tween({
        targets: this.backdrop,
        alpha: 1,
        ease: 'Quad.easeInOut',
        duration: 400
      });
      const scale = SMALL_PIECES.includes(piece.tile.index) ? 0.85 : 1;
      this.levelScene.shadow1
        .setPosition(piece.parentContainer.x, this.pieceOriginalY + 15)
        .setScale(scale)
        .setAlpha(1);
      this.tweens.add({
        targets: this.levelScene.shadow1,
        alpha: 0.6,
        scale: scale - 0.15,
        duration: 250,
        ease: 'Expo.EaseOut'
      });
      this.tweens.add({
        targets: [this.currentPiece.parentContainer],
        y: this.pieceOriginalY - 15,
        duration: 250,
        ease: 'Quad.EaseOut'
      });
      this.tweens.add({
        targets: [this.levelScene.selectedArrow],
        y: this.pieceOriginalY - 15 - 20,
        duration: 250,
        ease: 'Quad.EaseOut'
      });

      const start = () => {
        const scale = this.game.defaultZoom + 1;
        let offset = { x: 24 * scale, y: -this.rect.height + 16 };
        let tileX = (piece.tile.x + 1) * (32 * scale);
        let tileY = (piece.tile.y + 1) * (32 * scale);
        let camLeft =
          cam.worldView.centerX * scale - (cam.worldView.width * scale) / 2;
        let camTop =
          cam.worldView.centerY * scale - (cam.worldView.height * scale) / 2;
        let rectX = camLeft * -1 + tileX + offset.x;
        let rectY = camTop * -1 + tileY + offset.y;
        if (rectY < 50) {
          rectY += this.rect.height - scale * 60;
          this.tooTall = true;
        } else {
          this.tooTall = false;
        }
        this.rect.x = Math.floor(rectX);
        this.rect.y = Math.floor(rectY);
        this.renderModal(1);
      };

      const cam = this.levelScene.cameras.main;
      let zoom = cam.zoom || 1;
      this.lastZoom = zoom;
      cam.pan(
        piece.parentContainer.x,
        piece.parentContainer.y,
        400,
        'Quad.easeInOut'
      );
      cam.zoom !== this.game.defaultZoom + 1
        ? cam.zoomTo(
            this.game.defaultZoom + 1,
            400,
            'Quad.easeInOut',
            true,
            (cam, progress) => {
              if (progress === 1) {
                start();
              }
            }
          )
        : start();
    }
  };
  resetData = () => {
    this.typeLabel.setAlpha(0);
    this.nameLabel.setAlpha(0);
    this.hpLabel.setAlpha(0);
    this.hpPips.getChildren().forEach(child => {
      child.setAlpha(0);
    });
    this.pointy.setAlpha(0);
    this.movesetGroup.getChildren().forEach(child => {
      child.setAlpha(0);
    });
    this.bg.setAlpha(0);

    this.add.tween({
      targets: this.backdrop,
      alpha: 0,
      ease: 'Quad.easeInOut',
      duration: 400
    });

    let cam = this.levelScene.cameras.main;
    let x, y;
    let zoom = this.lastZoom;

    const {
      currentActiveRooms,
      currentSelectedPiece,
      inZone,
      levelData
    } = this.levelScene;

    if (currentActiveRooms) {
      if (currentActiveRooms.rooms.length > 1) {
        const centerOn = currentActiveRooms.rooms.find(room => {
          return room.find(rect => {
            return tileInRect(currentSelectedPiece.tile, rect);
          });
        });
        if (centerOn) {
          const rect = centerOn[0];
          x = rect.x + rect.w / 2;
          y = rect.y + rect.h / 2;
        }
      } else {
        const { centerX, centerY } = currentActiveRooms.centerOnRect;
        x = centerX;
        y = centerY;
      }
    } else if (inZone) {
      x = inZone.rect.x + inZone.rect.w / 2;
      y = inZone.rect.y + inZone.rect.h / 2;
    } else {
      x = levelData.lvlData.ground[0].length / 2;
      y = levelData.lvlData.ground.length / 2;
    }

    let center = {
      x: x * 32 + 16,
      y: y * 32 + 16
    };
    if (
      this.levelScene.scene.key !== 'WorldScene' ||
      this.levelScene.battlingInWorld
    ) {
      cam.pan(center.x, center.y, 400, 'Quad.easeInOut', true);
    }
    const next = () => {
      this.hiding = false;
      this.showing = false;
      if (
        this.currentPiece &&
        this.currentPiece.scene &&
        typeof this.currentPiece.setDepth === 'function'
      ) {
        this.currentPiece.setDepth(10);
      }
      this.currentPiece = null;
    };
    cam.zoom !== zoom
      ? cam.zoomTo(zoom, 400, 'Quad.easeInOut', true, (cam, progress) => {
          if (progress === 1) {
            next();
          }
        })
      : next();
  };
  hide = () => {
    if (this.currentPiece && this.showing) {
      const scale = SMALL_PIECES.includes(this.currentPiece.tile?.index)
        ? 0.85
        : 1;
      this.tweens.add({
        targets: [this.currentPiece.parentContainer],
        y: this.pieceOriginalY,
        duration: 250,
        ease: 'Quad.EaseOut',
        onComplete: this.resetData
      });
      this.tweens.add({
        targets: [this.levelScene.selectedArrow],
        y: this.pieceOriginalY - 20,
        duration: 250,
        ease: 'Quad.EaseOut'
      });
      this.tweens.add({
        targets: [this.levelScene.shadow1],
        alpha: 0,
        scale,
        duration: 250,
        ease: 'Quad.EaseOut'
      });
    }
  };
  handleClick = () => {
    if (!this.hiding) {
      this.hiding = true;
      this.hide();
    }
  };
}
